import { Mail, Phone, Place } from '@mui/icons-material';
import { Stack, type Theme, useMediaQuery } from '@mui/material';
import { useMemo, useState } from 'react';
import MobileHeader from '/@/components/bayer/header/mobile-header';
import PageContainer from '/@/components/bayer/page-container';
import Tabs from '/@/components/bayer/tabs';
import MoreOptions from '/@/components/more-options';
import useBreadcrumbs from '/@/hooks/breadcrumbs';
import { useAdvisorRvLinks, useGetUserNode } from '/@/hooks/nodes';
import { useIsValidatedConsultant } from '/@/hooks/session';
import { _ } from '/@/locale';
import { objectEntries } from '/@/utils';
import Header from '../../../../components/bayer/header';
import AncestorsBar from '../../components/ancestors-bar';
import { CurrentCampaignDashboard } from '../../growers/tabs/current-campaign';
import { PastReportsDashboard } from '../../growers/tabs/past-campaign';
import { RecommendationsDashboard } from '../../growers/tabs/recommendations';
import { type GrowersTabType, useTabs } from '../../index';

const RVDetail = ({ rvId }: { rvId: string }) => {
    const { tab, handleTabChange } = useTabs();
    const isSmOrLower = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [newFieldGrowersModalOpen, setNewFieldGrowersModalOpen] = useState(false);
    const [newGrowersModalOpen, setNewGrowersModalOpen] = useState(false);
    const [newFieldModalGrowerId, setNewFieldModalGrowerId] = useState<number | undefined>(undefined);
    const rvQuery = useGetUserNode(Number(rvId));

    const contactItems = useMemo(
        () =>
            [
                { key: 'location', Icon: Place, value: rvQuery.data?.location },
                { key: 'email', Icon: Mail, value: rvQuery.data?.email },
                { key: 'phone', Icon: Phone, value: rvQuery.data?.phone },
            ].filter((item): item is { key: string; Icon: typeof Place; value: string } => Boolean(item.value)),
        [rvQuery.data?.location, rvQuery.data?.email, rvQuery.data?.phone]
    );

    const tabs: Record<GrowersTabType, { label: string; disabled: boolean; component?: React.ReactNode }> = {
        'current-campaign': {
            label: _('Campaña actual'),
            disabled: false,
            component: (
                <CurrentCampaignDashboard
                    newGrowersModalOpen={newGrowersModalOpen}
                    setNewGrowersModalOpen={setNewGrowersModalOpen}
                    newFieldGrowersModalOpen={newFieldGrowersModalOpen}
                    setNewFieldGrowersModalOpen={setNewFieldGrowersModalOpen}
                    newFieldModalGrowerId={newFieldModalGrowerId}
                    setNewFieldModalGrowerId={setNewFieldModalGrowerId}
                    rvUserNodeQuery={rvQuery}
                    defaultRvId={rvId}
                    showFieldsByStageCard={true}
                />
            ),
        },
        'past-reports': {
            label: _('Reportes de campaña'),
            disabled: false,
            component: <PastReportsDashboard rvUserNodeQuery={rvQuery} />,
        },
        recommendations: {
            label: _('Recomendaciones'),
            disabled: false,
            component: <RecommendationsDashboard rvUserNodeQuery={rvQuery} />,
        },
    } as const;
    const breadcrumbs = useBreadcrumbs('rvs.RVDetail');
    const isConsultant = useIsValidatedConsultant();

    const advisorRVLinksQuery = useAdvisorRvLinks();
    const advisors = useMemo(() => {
        return (advisorRVLinksQuery.data?.results ?? [])
            .filter((link) => link.rv.id === Number(rvId))
            .map((link) => link.advisor);
    }, [advisorRVLinksQuery.data, rvId]);

    return (
        <PageContainer
            header={
                isSmOrLower ? (
                    <MobileHeader
                        breadcrumbs={breadcrumbs}
                        title={<span className='mp-mask'>{rvQuery.data?.name}</span>}
                        moreButton={
                            isConsultant && tab === 'current-campaign' ? (
                                <MoreOptions
                                    menuItems={[
                                        {
                                            label: _('Agregar productor'),
                                            onClick: () => setNewGrowersModalOpen(true),
                                        },
                                    ]}
                                />
                            ) : undefined
                        }
                    />
                ) : (
                    <Header
                        title={<span className='mp-mask'>{rvQuery.data?.name}</span>}
                        subtitle={
                            isConsultant
                                ? _('Obtené una visión integral del RV para brindar el asesoramiento que necesita.')
                                : _('Accedé a una visión integral de los productores del RV y su equipo de trabajo.')
                        }
                        breadcrumbs={breadcrumbs}
                        buttons={
                            isConsultant && tab === 'current-campaign'
                                ? [
                                      {
                                          children: _('Agregar productor'),
                                          variant: 'contained',
                                          color: 'secondary',
                                          size: 'medium',
                                          onClick: () => setNewGrowersModalOpen(true),
                                      },
                                  ]
                                : []
                        }
                    />
                )
            }
        >
            <AncestorsBar contactItems={contactItems} ancestorsFromNodeId={Number(rvId)} additionalNodes={advisors} />
            <Stack direction='column' spacing={3} mt={4}>
                <Tabs
                    options={objectEntries(tabs).map(([value, tab]) => ({
                        label: tab.label,
                        value,
                        disabled: tab.disabled,
                    }))}
                    value={tab}
                    setValue={(value) => handleTabChange(value)}
                    tabsVariant={isSmOrLower ? 'scrollable' : 'fullWidth'}
                />
                {tabs[tab].component}
            </Stack>
        </PageContainer>
    );
};

export default RVDetail;
